import React from 'react'
import moment from 'moment'
import Calendar from '@/components/calendar/Calendar'
import { ISelection } from '@/components/listSelection/ListSelection'
import {
  IExistingScaleInterval,
  ICellConfig,
  ECellColor,
  getTimeslotColor,
  EBadgeType,
} from '@/components/calendar/calendarTypes'
import utils from '@/components/calendar/utils'
import { IFullSchema } from '@/store/apis/schemas'
import {
  useLazyAddTimeslotsQuery,
  useLazyUpdateTimeslotQuery,
  useLazyDeleteTimeslotsQuery,
  ETimeslotIdTypes,
} from '@/store/apis/timeslots'
import { IUserObject } from '@/store/apis/users'
import { IUser } from '@/store/apis/scenes'
import { IValue } from '@/components/dropDown/DropDown'

interface IScenesSchemaProps {
  isLoading: boolean
  onlyPrioritised: boolean
  firstDay: moment.Moment
  lastDay: moment.Moment
  schemaData: IFullSchema
  selectedSceneId: number
  placeDropDownData: IValue[]
  onGetData(): void
  setFirstDay(newFirstDay: moment.Moment): void
}

const ScenesSchema: React.FC<IScenesSchemaProps> = ({
  isLoading,
  onlyPrioritised,
  firstDay,
  lastDay,
  schemaData,
  selectedSceneId,
  placeDropDownData,
  onGetData,
  setFirstDay,
}) => {
  const [addTimeslotsQuery] = useLazyAddTimeslotsQuery()
  const [updatTimeslotQuery] = useLazyUpdateTimeslotQuery()
  const [deleteTimeslotsQuery] = useLazyDeleteTimeslotsQuery()
  const [timeslots, setTimeslots] = React.useState<IExistingScaleInterval[]>([])
  const [underlyingCellConfigs, setUnderlyingCellConfigs] = React.useState<
    ICellConfig[]
  >([])
  const [userSelections, setUserSelections] = React.useState<ISelection[]>([])

  const sceneData = schemaData?.scenes?.find(
    (scene) => scene.scene.id === selectedSceneId,
  )

  React.useEffect(() => {
    if (schemaData) {
      const existingCells: ICellConfig[] = []
      schemaData.schema.timeslots.map((timeslot) => {
        const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
          firstDay,
          lastDay,
        )
        if (isInsideInterval) {
          const intervals = utils.getIntervalsByDuration(
            30,
            moment(timeslot.startTimestamp),
            moment(timeslot.endTimestamp),
          )
          intervals.map((interval) => {
            existingCells.push({
              startTimestamp: interval.start,
              color: getTimeslotColor(timeslot),
              badge: timeslot.comment && {
                type: EBadgeType.Scene,
                contents: [
                  {
                    label: timeslot.comment,
                    comment: '',
                  },
                ],
              },
            })
          })
        }
      })

      // Users
      const users = sceneData?.users?.reduce((userList, user) => {
        const foundIndex = schemaData.users.findIndex(
          (schemaUser) => schemaUser.id === user.id,
        )
        if (
          onlyPrioritised &&
          sceneData.scene.userPriorities?.findIndex(
            (userPriorityId) => userPriorityId === user.id,
          ) > -1
        ) {
          userList.push(schemaData.users[foundIndex])
        } else if (!onlyPrioritised && foundIndex !== -1) {
          userList.push(schemaData.users[foundIndex])
        }
        return userList
      }, [] as IUser[])
      users?.map((user) =>
        user.timeslots.map((timeslot) => {
          const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
            firstDay,
            lastDay,
          )
          if (isInsideInterval) {
            const intervals = utils.getIntervalsByDuration(
              30,
              moment(timeslot.startTimestamp),
              moment(timeslot.endTimestamp),
            )
            intervals.map((interval) => {
              const startTimestamp = interval.start
              const founIndex = existingCells.findIndex(
                (existing) =>
                  moment(existing.startTimestamp).isSame(
                    startTimestamp,
                    'minute',
                  ) && existing.color === ECellColor.User,
              )
              if (founIndex === -1) {
                existingCells.push({
                  startTimestamp,
                  color: ECellColor.User,
                  badge: {
                    type: EBadgeType.User,
                    contents: [
                      {
                        label: `${user.firstName} ${user.lastName}`,
                        comment: timeslot.comment,
                      },
                    ],
                  },
                })
              } else {
                const { badge } = existingCells[founIndex]
                const badgeContents = [
                  {
                    label: `${user.firstName} ${user.lastName}`,
                    comment: timeslot.comment,
                  },
                ]
                existingCells[founIndex] = {
                  ...existingCells[founIndex],
                  badge: {
                    type: EBadgeType.User,
                    contents: badge
                      ? badge.contents.concat(badgeContents)
                      : badgeContents,
                  },
                }
              }
            })
          }
        }),
      )

      // Other scenes timeslots
      const existing: IExistingScaleInterval[] = []
      const scenes = [...schemaData.scenes].sort((a, b) =>
        a.scene.name < b.scene.name ? -1 : 1,
      )
      let userSelection: ISelection[] = []
      scenes.map((scene): void => {
        const isCurrentScene = scene.scene.id === selectedSceneId
        if (isCurrentScene) {
          scene.timeslots.map((timeslot) => {
            existing.push({
              id: timeslot.id,
              start: moment(timeslot.startTimestamp),
              end: moment(timeslot.endTimestamp),
              color: ECellColor.Scene,
              comment: timeslot.comment,
              placeId: timeslot.placeId,
            })
          })
          const users = scene.users.reduce((userList, user): IUserObject[] => {
            const foundIndex = schemaData.users.findIndex(
              (schemaUser) => schemaUser.id === user.id,
            )
            if (foundIndex !== -1) {
              userList.push(schemaData.users[foundIndex])
            }
            return userList
          }, [])
          userSelection = userSelection.concat(
            users.map((user) => ({
              id: user.id,
              name: `* ${user.firstName} ${user.lastName}`,
              defaultSelected: true,
            })),
          )
        } else {
          scene.timeslots.map((timeslot) => {
            const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
              firstDay,
              lastDay,
            )
            if (isInsideInterval) {
              const intervals = utils.getIntervalsByDuration(
                30,
                moment(timeslot.startTimestamp),
                moment(timeslot.endTimestamp),
              )
              intervals.map((interval, index) => {
                const startTimestamp = interval.start
                const founIndex = existingCells.findIndex(
                  (existing) =>
                    moment(existing.startTimestamp).isSame(
                      startTimestamp,
                      'minute',
                    ) && existing.color === ECellColor.OtherScene,
                )
                if (founIndex === -1) {
                  existingCells.push({
                    isStart: index === 0,
                    isEnd: index === intervals.length - 1,
                    startTimestamp,
                    color: ECellColor.OtherScene,
                    badge: {
                      type: EBadgeType.Scene,
                      contents: [
                        {
                          label: scene.scene.name,
                          comment: timeslot.comment,
                        },
                      ],
                    },
                  })
                } else {
                  const { badge } = existingCells[founIndex]
                  existingCells[founIndex] = {
                    ...existingCells[founIndex],
                    badge: {
                      type: EBadgeType.Scene,
                      contents: badge.contents.concat([
                        {
                          label: scene.scene.name,
                          comment: timeslot.comment,
                        },
                      ]),
                    },
                  }
                }
              })
            }
          })
        }
      })

      schemaData.users.map((user) => {
        const foundId = userSelection.findIndex(
          (selUser) => selUser.id === user.id,
        )
        if (foundId === -1) {
          userSelection.push({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            defaultSelected: false,
          })
        }
      })

      setUserSelections(userSelection)

      // Scene groups
      const sceneGroups = [...schemaData.sceneGroups].sort((a, b) =>
        a.sceneGroup.name < b.sceneGroup.name ? -1 : 1,
      )
      sceneGroups.map((sceneGroup) =>
        sceneGroup.timeslots.map((timeslot) => {
          const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
            firstDay,
            lastDay,
          )
          if (isInsideInterval) {
            const intervals = utils.getIntervalsByDuration(
              30,
              moment(timeslot.startTimestamp),
              moment(timeslot.endTimestamp),
            )
            intervals.map((interval, index) => {
              const startTimestamp = interval.start
              const founIndex = existingCells.findIndex(
                (existing) =>
                  moment(existing.startTimestamp).isSame(
                    startTimestamp,
                    'minute',
                  ) && existing.color === ECellColor.OtherScene,
              )
              if (founIndex === -1) {
                existingCells.push({
                  isStart: index === 0,
                  isEnd: index === intervals.length - 1,
                  startTimestamp,
                  color: ECellColor.OtherScene,
                  badge: {
                    type: EBadgeType.Scene,
                    contents: [
                      {
                        label: sceneGroup.sceneGroup.name,
                        comment: timeslot.comment,
                      },
                    ],
                  },
                })
              } else {
                const { badge } = existingCells[founIndex]
                existingCells[founIndex] = {
                  ...existingCells[founIndex],
                  badge: {
                    type: EBadgeType.Scene,
                    contents: badge.contents.concat([
                      {
                        label: sceneGroup.sceneGroup.name,
                        comment: timeslot.comment,
                      },
                    ]),
                  },
                }
              }
            })
          }
        }),
      )
      setUnderlyingCellConfigs(existingCells)
      setTimeslots(existing)
    }
  }, [schemaData, sceneData, selectedSceneId, firstDay, lastDay])

  const handleAddTimeslot = async (
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
  ): Promise<void> => {
    await addTimeslotsQuery({
      parentType: ETimeslotIdTypes.Scene,
      schemaId: schemaData.schema.schema.id,
      parentId: sceneData.scene.id,
      timeslots: [
        {
          startTimestamp: startTimestamp.toISOString(),
          endTimestamp: endTimestamp.toISOString(),
          comment,
          placeId,
        },
      ],
    })
    onGetData()
  }

  const handleUpdateTimeslot = async (
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
  ): Promise<void> => {
    await updatTimeslotQuery({
      id,
      parentType: ETimeslotIdTypes.Scene,
      parentId: sceneData.scene.id,
      timeslot: {
        startTimestamp: startTimestamp.toISOString(),
        endTimestamp: endTimestamp.toISOString(),
        comment,
        placeId,
      },
    })
    onGetData()
  }

  const handleDeleteTimeslots = async (ids: number[]): Promise<void> => {
    await deleteTimeslotsQuery({
      schemaId: schemaData.schema.schema.id,
      ids,
    })
    onGetData()
  }

  return (
    <Calendar
      isLoading={isLoading}
      firstDay={firstDay ?? moment()}
      onFirstDayUpdate={(newFirstDay) => setFirstDay(newFirstDay)}
      minDay={
        schemaData ? moment(schemaData.schema.schema.startDate) : undefined
      }
      maxDay={schemaData ? moment(schemaData.schema.schema.endDate) : undefined}
      selections={userSelections}
      createColor={ECellColor.Scene}
      selectedIntervals={timeslots}
      allowOverlap
      showPlaceOnEvent
      underlyingCellConfigs={underlyingCellConfigs}
      placeData={placeDropDownData}
      onIntervalSelect={handleAddTimeslot}
      onIntervalUpdate={handleUpdateTimeslot}
      onIntervalsRemove={handleDeleteTimeslots}
      calendarDayComment={
        schemaData
          ? schemaData.schema.days.map((day) => ({
              date: new Date(day.date),
              comment: day.comment,
            }))
          : []
      }
    />
  )
}

export default ScenesSchema
